import React, { useState, useRef } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';

const ProfilePictureUpload = ({ currentImage, onImageUpdate, className = "" }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(currentImage);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleImageUpload = async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      // Convert to base64 for storage
      const base64 = await convertToBase64(file);
      
      // Update preview immediately
      setPreviewImage(base64);
      
      // Call parent component to update the profile
      if (onImageUpdate) {
        await onImageUpdate(base64);
      }

    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      setPreviewImage(currentImage); // Revert preview on error
    } finally {
      setIsUploading(false);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    if (onImageUpdate) {
      onImageUpdate(null);
    }
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        capture="environment" // This helps mobile devices default to camera
      />

      {/* Profile Picture Display */}
      <div className="relative w-32 h-32 mx-auto">
        <div className="w-full h-full rounded-full overflow-hidden bg-gray-200 border-4 border-white shadow-lg">
          {previewImage ? (
            <img
              src={previewImage}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <Camera className="w-8 h-8 text-blue-400" />
            </div>
          )}
        </div>

        {/* Upload Button Overlay */}
        <button
          onClick={handleCameraClick}
          disabled={isUploading}
          className="absolute bottom-0 right-0 w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors border-2 border-white"
        >
          {isUploading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Camera className="w-4 h-4" />
          )}
        </button>

        {/* Remove Button (only show if image exists) */}
        {previewImage && (
          <button
            onClick={handleRemoveImage}
            className="absolute top-0 right-0 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors border-2 border-white"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Upload Instructions */}
      <div className="mt-4 text-center">
        <p className="text-sm text-gray-600 mb-2">
          Click the camera icon to upload a photo
        </p>
        <div className="flex justify-center gap-2">
          <Button
            onClick={handleCameraClick}
            disabled={isUploading}
            variant="outline"
            size="sm"
            className="text-xs"
          >
            <Upload className="w-3 h-3 mr-1" />
            Choose File
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Supports JPG, PNG, GIF • Max 5MB
        </p>
      </div>
    </div>
  );
};

export default ProfilePictureUpload;